import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, ErrorState, Icon, Select, Tabs, TabsContent } from "@sagot/ui";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { useDebounced } from "../../../lib/slug";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { PageHeader } from "../shared/page-header";
import { useAgentList } from "../shared/use-engage-data";
import { WriteAnswerSheet } from "./answer-sheet";
import { GapBulkBar } from "./gap-bulk-bar";
import { countByTab, GAP_TABS, inTab, type GapSort, type GapTab, type GapView } from "./gap-model";
import { GapRowsSkeleton, OpenGapPanel, ResolvedGapPanel } from "./gap-panels";
import { GapStatsGrid } from "./gap-stats";
import { GapToolbar, isGapTab } from "./gap-toolbar";
import { IgnoreGapDialog } from "./ignore-dialog";
import { useAssignableMembers, useGapActions } from "./use-gap-actions";
import { useGapStats, useOpenGaps, useResolvedGaps } from "./use-gaps";

const GAP_PARAM = "gap";

/** `/w/:ws/gaps` — knowledge gap clusters: answer, assign, ignore, export. */
export function GapsPage() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const permissions = usePermissions();
  const canResolve = permissions.can("gaps.resolve") && !permissions.readOnly;
  const members = useAssignableMembers(canResolve && permissions.can("team.view"));
  const agents = useAgentList();
  const [params, setParams] = useSearchParams();
  const [agentId, setAgentId] = useState<string | null>(null);
  const [tab, setTab] = useState<GapTab>("unanswered");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<GapSort>("most_asked");
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [writing, setWriting] = useState<GapView | null>(null);
  const filters = { agentId, q: useDebounced(q), sort };
  const open = useOpenGaps(filters);
  const resolved = useResolvedGaps(filters, tab === "resolved");
  const stats = useGapStats();
  const clearSelection = () => {
    setSelected(new Set());
  };
  const actions = useGapActions(clearSelection);

  // `?gap=:id` (dashboard "Answer" links) opens that cluster's answer sheet once.
  const [handledLink, setHandledLink] = useState<string | null>(null);
  const deepLinked = params.get(GAP_PARAM);
  const linkedGap = deepLinked && deepLinked !== handledLink ? open.data?.find((g) => g.id === deepLinked) : undefined;
  if (linkedGap && canResolve) {
    setHandledLink(linkedGap.id);
    setWriting(linkedGap);
  }

  const uploadAgent = agentId ?? agents.data?.[0]?.id ?? null;
  const uploadHref = (gap: GapView | null) => {
    const target = gap?.agent_id ?? uploadAgent;
    return target ? `${consoleRoutes.knowledge(summary.slug, target)}${gap ? `?resolvesGap=${encodeURIComponent(gap.id)}` : ""}` : null;
  };
  const counts = open.data ? { ...countByTab(open.data), resolved: stats.data?.resolved_this_month } : { resolved: stats.data?.resolved_this_month };
  const visible = open.data && tab !== "resolved" ? inTab(open.data, tab) : [];
  const selectedVisible = visible.filter((g) => selected.has(g.id)).map((g) => g.id);

  const select = (ids: readonly string[], value: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (value) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };
  const closeSheet = (isOpen: boolean) => {
    if (isOpen) return;
    setWriting(null);
    if (params.has(GAP_PARAM)) setParams((p) => { p.delete(GAP_PARAM); return p; }, { replace: true });
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <PageHeader
        title={t("engage.gaps.title")}
        subtitle={t("engage.gaps.subtitle")}
        actions={
          <>
            <Select className="h-9 w-48" aria-label={t("engage.gaps.agentFilter")} value={agentId ?? ""} onChange={(e) => { setAgentId(e.target.value || null); clearSelection(); }}>
              <option value="">{t("engage.allAgents")}</option>
              {(agents.data ?? []).map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}
                </option>
              ))}
            </Select>
            <Button variant="secondary" loading={actions.exporting} onClick={() => { actions.download(agentId); }}>
              <Icon name="download" />
              {t("engage.gaps.export")}
            </Button>
            {canResolve && uploadHref(null) ? (
              <Button asChild>
                <AppLink href={uploadHref(null) ?? ""}>
                  <Icon name="upload" />
                  {t("engage.gaps.uploadDocument")}
                </AppLink>
              </Button>
            ) : null}
          </>
        }
      />
      <GapStatsGrid />
      <section className="card overflow-hidden" aria-label={t("engage.gaps.tabs.label")}>
        <Tabs value={tab} onValueChange={(value) => { if (isGapTab(value)) { setTab(value); clearSelection(); } }}>
          <GapToolbar counts={counts} q={q} onQueryChange={setQ} sort={sort} onSortChange={setSort} />
          {tab !== "resolved" && canResolve ? (
            <GapBulkBar
              count={selectedVisible.length}
              members={members}
              pending={actions.bulkPending}
              onAssign={(member) => { actions.assignMany(selectedVisible, member); }}
              onIgnore={() => { actions.setIgnoreTarget({ kind: "bulk", ids: selectedVisible }); }}
              onClear={clearSelection}
            />
          ) : null}
          {GAP_TABS.map((value) => (
            <TabsContent key={value} value={value} forceMount hidden={value !== tab}>
              {value !== tab ? null : value === "resolved" ? (
                <ResolvedGapPanel query={resolved} />
              ) : open.isPending ? (
                <GapRowsSkeleton />
              ) : open.isError ? (
                <ErrorState headingLevel={3} title={t("engage.gaps.loadError")} onRetry={() => void open.refetch()} retryLabel={t("common.retry")} />
              ) : (
                <OpenGapPanel
                  gaps={visible}
                  selected={selected}
                  onSelect={select}
                  searching={q.trim() !== ""}
                  onClearSearch={() => { setQ(""); }}
                  canResolve={canResolve}
                  members={members}
                  memberName={(userId) => (userId ? (members?.find((m) => m.userId === userId)?.name ?? null) : null)}
                  uploadHref={canResolve ? uploadHref : () => null}
                  onWrite={setWriting}
                  onIgnore={(gap) => { actions.setIgnoreTarget({ kind: "one", gap }); }}
                  onAssign={actions.assignOne}
                />
              )}
            </TabsContent>
          ))}
        </Tabs>
      </section>
      <p className="flex items-start gap-2 text-xs text-fg-subtle">
        <Icon name="info" className="mt-0.5 size-3.5 shrink-0" />
        {t("engage.gaps.footnote")}
      </p>
      <WriteAnswerSheet gap={writing} onOpenChange={closeSheet} uploadHref={writing ? uploadHref(writing) : null} />
      <IgnoreGapDialog
        open={actions.ignoreTarget !== null}
        onOpenChange={(isOpen) => { if (!isOpen) actions.setIgnoreTarget(null); }}
        pending={actions.ignorePending}
        onConfirm={actions.confirmIgnore}
      />
    </div>
  );
}
